/**
 * Auth Service - Basic Authentication
 *
 * Handles basic authentication operations: signup, signin, signout, password reset
 */

import { supabase } from "@/integrations/supabase/client";
import { BaseService } from "../base/BaseService";
import {
  AuthResponse,
  SignupProfile,
  SignoutResponse,
  ResetPasswordResponse,
  TrackReferralSignupResponse,
} from "./types";
import { MFAService } from "./mfaService";
import { PasswordService } from "./passwordService";
import { buildSignupErrorResponse, buildSignupRequestBody } from "./authServiceHelpers";
/**
 * Main Authentication Service
 * Handles basic authentication operations
 */
export class AuthService extends BaseService {
  private readonly mfaService: MFAService;
  private readonly passwordService: PasswordService;

  constructor() {
    super("auth");
    this.mfaService = new MFAService();
    this.passwordService = new PasswordService();
  }

  /**
   * Expose getAuthHeaders for use by other services
   */
  public async getAuthHeaders(includeCSRF: boolean = true) {
    const headers = await super.getAuthHeaders(includeCSRF);
    return headers;
  }

  /**
   * Sign up a new user via auth-service API (no direct Supabase Auth call).
   * POST /signup → auth-service → Supabase (Before User Created hook runs server-side).
   * On success, sets session in browser so the user is logged in.
   */
  async signup(
    email: string,
    password: string,
    profile?: SignupProfile,
    captchaToken?: string
  ): Promise<AuthResponse> {
    const body = buildSignupRequestBody(email, password, profile, captchaToken);

    try {
      const data = (await this.post<{
        user?: AuthResponse["user"];
        session?: AuthResponse["session"];
      }>("/signup", body, { includeCSRF: false })) as {
        user?: AuthResponse["user"];
        session?: AuthResponse["session"];
      };

      if (data?.session?.access_token && data?.session?.refresh_token) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }

      return { user: data?.user ?? null, session: data?.session ?? null };
    } catch (err) {
      return buildSignupErrorResponse(err);
    }
  }

  /**
   * Sign in a user
   * POST /signin
   *
   * Note: We use Supabase for the actual signin (it manages sessions),
   * but the auth-service can add business logic checks
   */
  async signin(email: string, password: string): Promise<AuthResponse> {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const lockoutStatus = await this.checkAccountLockoutViaSecurity(normalizedEmail);
      if (lockoutStatus.locked) {
        return {
          error: {
            message:
              "Account temporarily locked due to too many failed login attempts. Try again in 15 minutes.",
            code: "ACCOUNT_LOCKED",
          },
        };
      }

      // Step 1: Authenticate with Supabase (creates session)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        await this.logLoginAttemptViaSecurity(normalizedEmail, false, error.message);
        const postFailLockout = await this.checkAccountLockoutViaSecurity(normalizedEmail);
        if (postFailLockout.locked) {
          return {
            error: {
              message:
                "Account temporarily locked due to too many failed login attempts. Try again in 15 minutes.",
              code: "ACCOUNT_LOCKED",
            },
          };
        }
        return { error };
      }

      // Step 2: Log successful login to auth-service
      if (data.user) {
        await this.logLoginAttemptViaSecurity(normalizedEmail, true, undefined, data.user.id);
      }

      // Step 3: Check for MFA requirement
      const { session } = data;
      if (session?.user) {
        // Check if MFA is enabled and not yet verified
        const mfaStatus = await this.mfaService.getMFAStatus(session.user.id);

        // Simplified MFA check - rely on factors list
        if (mfaStatus.mfaEnabled) {
          // MFA is required but not yet verified
          return {
            user: data.user,
            session: data.session,
            mfaRequired: true,
          };
        }
      }

      // Step 4: Check password expiry
      if (data.user) {
        const expiryInfo = await this.passwordService.checkPasswordExpiry(data.user.id);
        if (expiryInfo.expired) {
          // Password expired - force sign out
          await supabase.auth.signOut();
          return {
            error: {
              message: "Your password has expired. Please reset it to continue.",
              code: "PASSWORD_EXPIRED",
            },
          };
        }
      }

      return { user: data.user, session: data.session };
    } catch (error) {
      console.error("Signin error:", error);
      return { error };
    }
  }

  /**
   * Sign out current user
   * POST /signout
   */
  async signout(): Promise<SignoutResponse> {
    const { error } = await supabase.auth.signOut();
    return { error };
  }

  /**
   * Reset password
   * POST /reset-password
   * Uses API endpoint instead of direct Supabase call to support CAPTCHA verification
   */
  async resetPassword(email: string, captchaToken?: string): Promise<ResetPasswordResponse> {
    try {
      const response = await this.post<{ message?: string; error?: string }>("/reset-password", {
        email,
        captchaToken,
      });

      // If API returns an error message, convert it to an error object
      if (response.error) {
        return {
          error: {
            message: response.error,
            name: "PasswordResetError",
          } as Error,
        };
      }

      return {};
    } catch (error) {
      return { error: error as Error };
    }
  }

  private async checkAccountLockoutViaSecurity(email: string) {
    const { SecurityService } = await import("./securityService");
    const securityService = new SecurityService();
    return securityService.checkAccountLockout(email);
  }

  private async logLoginAttemptViaSecurity(
    email: string,
    success: boolean,
    failureReason?: string,
    userId?: string
  ): Promise<void> {
    const { SecurityService } = await import("./securityService");
    const securityService = new SecurityService();
    await securityService.logLoginAttempt({ email, success, failureReason, userId });
  }

  /**
   * Track referral signup
   * POST /utilities/track-referral-signup
   */
  async trackReferralSignup(
    refereeId: string,
    referralCode: string,
    source: string = "direct_link"
  ): Promise<TrackReferralSignupResponse> {
    const result = await this.post<TrackReferralSignupResponse>(
      "/utilities/track-referral-signup",
      {
        referee_id: refereeId,
        referral_code: referralCode,
        source,
      }
    );
    return result;
  }
}

// Export singleton instance
export const authService = new AuthService();
